import { Component, Input, OnInit } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StudyManagerModule } from '../study-manager.module';
import { CoreUtilsModule } from '../../shared/core-utils';
import { FieldmapService } from '../../field-management/service/fieldmap.service';
import { AlertService } from '../../shared/alert/alert.service';
import { EnvironmentFieldmapInfoModel } from './environment-fieldmap-info.model';
import { BmsjHipsterSharedLibsModule } from '../../shared';
import { StudyEditorService } from '../../study-editor/study-editor.service';
import { FieldMapContext } from './field-map.context';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';

@Component({
    selector: 'jhi-create-field-map-modal',
    standalone: true,
    imports: [
        NgSelectModule,
        TranslateModule,
        StudyManagerModule,
        CoreUtilsModule,
        BmsjHipsterSharedLibsModule
    ],
    templateUrl: './select-environment-field-map-modal.component.html'
})
export class SelectEnvironmentFieldMapModalComponent implements OnInit {

    @Input() studyId: any;
    @Input() studyName: any;
    @Input() isCreateFieldMap = false;
    @Input() isViewFieldMap = false;
    @Input() isDeleteFieldMap = false;
    isLoading: boolean = false;
    environmentFieldmapInfoItems: EnvironmentFieldmapInfoItem[] = [];
    selectedInstanceId: number = null;

    constructor(private activeModal: NgbActiveModal,
                private fieldmapService: FieldmapService,
                private alertService: AlertService,
                private studyEditorService: StudyEditorService,
                private fieldMapContext: FieldMapContext,
                private modalService: NgbModal,
                private translateService: TranslateService) {
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.fieldmapService.getEnvironmentFieldmapInfo(this.studyId, null).toPromise().then((fieldmapInfo)=> {
            this.environmentFieldmapInfoItems = fieldmapInfo as EnvironmentFieldmapInfoItem[];
            if (this.isViewFieldMap) {
                this.environmentFieldmapInfoItems = this.environmentFieldmapInfoItems.filter((env) => env.hasFieldMap);
            }
            this.isLoading = false;
        });
    }

    disableSelect(env: EnvironmentFieldmapInfoItem) {
        if (this.isCreateFieldMap || this.isDeleteFieldMap) {
            return env.hasGeoJSON || env.hasMeansData;
        } else {
            return !env.hasFieldMap;
        }
    }

    getSelectedInstanceIds(): number[] {
        return this.environmentFieldmapInfoItems.filter((instance) => instance.isChecked)
            .map((instance) => instance.instanceId);
    }

    getSelectedEnvironmentFieldmapInfo(): EnvironmentFieldmapInfoItem[] {
        return this.environmentFieldmapInfoItems.filter((instance) => instance.isChecked);
    }

    cancel() {
        this.activeModal.dismiss();
    }

    continue() {
        if (this.isCreateFieldMap && this.isSelectedInstancesForCreateValid()) {
            if (this.getSelectedEnvironmentFieldmapInfo().filter((instance) => instance.hasFieldMap).length) {
                const instanceIdsWithFieldMap = this.environmentFieldmapInfoItems.filter((instance) => instance.isChecked && instance.hasFieldMap)
                    .map((instance) => instance.instanceId);
                const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
                confirmModalRef.componentInstance.title = this.translateService.instant('study-editor.field-map.delete.title');
                confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.field-map.delete.confirm.message');
                confirmModalRef.result.then(() => {
                    this.fieldmapService.deleteFieldMap(this.studyId, instanceIdsWithFieldMap).toPromise()
                        .then((response) => {
                            this.navigateToFieldPlan();
                        });
                });
            } else {
                this.navigateToFieldPlan();
            }
        }
        if (this.isViewFieldMap) {
            this.fieldMapContext.selectedEnvironments = null;
            this.fieldMapContext.instanceId = this.selectedInstanceId;
            this.fieldMapContext.studyName = this.studyName;
            this.studyEditorService.navigateToFieldPlan(this.studyId);
            this.activeModal.dismiss();
        }
        if (this.isDeleteFieldMap) {
            const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
            confirmModalRef.componentInstance.title = this.translateService.instant('study-editor.field-map.delete.title');
            confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.field-map.delete.confirm.message');
            confirmModalRef.result.then(() => {
                this.fieldmapService.deleteFieldMap(this.studyId, this.getSelectedInstanceIds()).toPromise()
                    .then((response) => {
                        if (response.length) {
                            this.alertService.warning('study-editor.field-map.delete.warning', { param: response.join(', ') });
                        } else {
                            this.alertService.success('study-editor.field-map.delete.success');
                        }
                    });
                this.activeModal.close();
            });
        }
    }

    navigateToFieldPlan() {
        this.fieldMapContext.instanceId = null;
        this.fieldMapContext.selectedEnvironments = this.getSelectedEnvironmentFieldmapInfo();
        this.fieldMapContext.studyName = this.studyName;
        this.studyEditorService.navigateToFieldPlan(this.studyId);
        this.activeModal.dismiss();
    }

    isSelectedInstancesForCreateValid(): boolean {
        const selectedEnvironments = this.getSelectedEnvironmentFieldmapInfo();
        if (selectedEnvironments.length > 1) {
            const firstValue = selectedEnvironments[0].locationId;
            const isSimilarLocationIds = selectedEnvironments.every(env => env.locationId === firstValue);
            if (!isSimilarLocationIds) {
                this.alertService.error('study-editor.field-map.create.different.locations.error');
                return false;
            }
        }

        return true;
    }

    disableContinueButton() {
        if (this.isLoading) {
            return true;
        }
        if (this.isViewFieldMap) {
            return this.selectedInstanceId === null;
        } else {
            return this.getSelectedInstanceIds().length === 0;
        }
    }
}

export class EnvironmentFieldmapInfoItem extends EnvironmentFieldmapInfoModel {
    isChecked: boolean;
}
